// ============================================================================
// THEME CONFIGURATION — TENANT-AWARE PRESET LOADER
// ============================================================================
// The active theme is resolved from VITE_THEME env var:
//   VITE_THEME=dumax       → src/config/themes/dumax.json
//   VITE_THEME=central-gps → src/config/themes/central-gps.json
//
// Fallback chain: {tenant}.json → grayscale.json
//
// Usage:
//   import { brand, colors, fonts, assets } from "./config/theme";
//   import theme from "./config/theme";
// ============================================================================


// ---------------------------------------------------------------------------
// Load all theme JSON files eagerly at build time
// ---------------------------------------------------------------------------
const themeModules = import.meta.glob<{ default: ThemePreset }>(
  "./themes/*.json",
  { eager: true },
);

// ---------------------------------------------------------------------------
// Resolve active theme: {VITE_THEME}.json → grayscale.json
// ---------------------------------------------------------------------------
const tenant = import.meta.env.VITE_THEME as string | undefined;

function resolveThemeModule(key: string): ThemePreset | undefined {
  const mod = themeModules[key];
  if (!mod) return undefined;
  return (mod.default ?? mod) as unknown as ThemePreset;
}

const activePreset: ThemePreset =
  (tenant ? resolveThemeModule(`./themes/${tenant}.json`) : undefined) ??
  resolveThemeModule("./themes/grayscale.json")!;

// ---------------------------------------------------------------------------
// Type definition matching the JSON shape
// ---------------------------------------------------------------------------
export interface ThemePreset {
  id: string;
  name: string;
  brand: {
    primary: string;
    primaryHover: string;
    accent: string;
    logoFill: string;
    // Tono del primario para TEXTO sobre fondos claros (opcional). El primario
    // puede ser un relleno claro que como texto no se lee sobre blanco.
    primaryTextLight?: string;
  };
  colors: {
    primary: Record<string, string>;
    dark: { bg: string; surface: string; card: string; border: string; hover: string };
    light: { bg: string; surface: string; card: string; border: string; borderSecondary: string; hover: string };
    text: {
      dark: { primary: string; secondary: string; muted: string };
      light: { primary: string; secondary: string; muted: string; subtle: string };
    };
    status: { error: string; errorLight: string; success: string; warning: string; info: string };
    overlay: { loginBg: string; loginBgDark: string; imageOverlay: string };
  };
  fonts: {
    sans: string[];
    display: string[];
    googleFontsUrl: string;
  };
  assets: {
    // wordmark: logotipo horizontal (símbolo + nombre) para el encabezado.
    // Opcional: un tema sin él conserva el ícono de la app + título.
    logo: {
      main: string;
      splash: string;
      favicon: string;
      wordmark?: { light: string; dark: string };
    };
    backgrounds: { main: string };
    partner: { light: string; dark: string };
  };
  pwa: { name: string; shortName: string; description: string };
  shadows: { card: string; dropdown: string; logo: string };
  radii: Record<string, string>;
  zIndex: Record<string, number>;
}

// ---------------------------------------------------------------------------
// 1. BRAND COLORS
// ---------------------------------------------------------------------------
export const brand = activePreset.brand;

// Color de TEXTO del primario según el tema (ThemeContext publica
// --brand-text). Para estilos en línea; las clases text-primary-* lo reciben
// por la regla de index.css.
export const brandTextColor = `var(--brand-text, ${activePreset.brand.primary})`;

// ---------------------------------------------------------------------------
// 2. COLOR PALETTE
// ---------------------------------------------------------------------------
export const colors = activePreset.colors;

// ---------------------------------------------------------------------------
// 3. TYPOGRAPHY (merged with icon URLs that don't change per theme)
// ---------------------------------------------------------------------------
export const fonts = {
  ...activePreset.fonts,
  icons: {
    remixicon: "https://cdn.jsdelivr.net/npm/remixicon@4.3.0/fonts/remixicon.css",
    materialIcons: "https://fonts.googleapis.com/icon?family=Material+Icons",
    materialSymbols:
      "https://fonts.googleapis.com/css2?family=Material+Symbols+Outlined",
  },
};

// ---------------------------------------------------------------------------
// 4. LOGO & BRAND ASSETS (filenames — actual imports in brandAssets.ts)
// ---------------------------------------------------------------------------
export const assets = activePreset.assets;

// ---------------------------------------------------------------------------
// 5. SHADOWS & EFFECTS
// ---------------------------------------------------------------------------
export const shadows = activePreset.shadows;

// ---------------------------------------------------------------------------
// 6. BORDER RADIUS
// ---------------------------------------------------------------------------
export const radii = activePreset.radii;

// ---------------------------------------------------------------------------
// 7. Z-INDEX SCALE
// ---------------------------------------------------------------------------
export const zIndex = activePreset.zIndex;

// ---------------------------------------------------------------------------
// 8. PWA / META
// ---------------------------------------------------------------------------
export const pwa = {
  ...activePreset.pwa,
  themeColor: brand.accent,
  backgroundColor: colors.light.bg,
};

// ---------------------------------------------------------------------------
// 9. GRADIENT PRESETS (computed from active brand)
// ---------------------------------------------------------------------------
export const gradients = {
  logoBg: `linear-gradient(to bottom right, ${brand.primary}, ${brand.primaryHover})`,
  // Use static Tailwind classes (from-primary-500 to-primary-400) in components instead.
  // Dynamic class interpolation like `from-[${hex}]` breaks Tailwind JIT.
  logoBgClass: "from-primary-500 to-primary-400",
};

// ---------------------------------------------------------------------------
// 10. ACTIVE THEME ID (useful for conditional logic in components)
// ---------------------------------------------------------------------------
export const activeThemeId = activePreset.id;

// ---------------------------------------------------------------------------
// 11. AGGREGATED THEME OBJECT
// ---------------------------------------------------------------------------
export const theme = {
  id: activePreset.id,
  name: activePreset.name,
  brand,
  colors,
  fonts,
  assets,
  shadows,
  radii,
  zIndex,
  pwa,
  gradients,
};

export type Theme = typeof theme;

export default theme;
